/**
 * Breadcrumb order for any page: Home → Category → Type → City → Location(s) → Project
 * Each segment is shown when present in URL/context.
 * When multiple locations are searched (multi param), they appear as one segment e.g. "Kokapet, Gachibowli".
 */

import { decodeMultiParam } from './multiSearch';
import { getCityForLocationSync } from './locationToCity';

export const ROUTE_LABELS = {
  'create-account': 'Create Account',
  'login': 'Login',
  'edit-information': 'Edit Profile',
  'notifications': 'Notifications',
  'like': 'Shortlisted',
  'apts-like': 'Shortlisted',
  'villas-like': 'Shortlisted',
  'plots-like': 'Shortlisted',
  'rents-like': 'Shortlisted',
  'contacted-properties': 'Contacted Properties',
  'saved-properties': 'Saved Properties',
  'saved-property-news': 'Saved property news',
  'shortlisted-properties': 'Shortlisted',
  'viewed-properties': 'Viewed',
  'recommended-properties': 'Recommended',
  'about-company': 'About Us',
  'contact-us': 'Contact Us',
  'terms-and-conditions': 'Terms & Conditions',
  'privacy-policy': 'Privacy Policy',
  'faqs': 'FAQs',
  'property-assistance': 'Property Assistance',
  'property-verification': 'Property Verification',
  'other-property-services': 'Other Services',
  'apts-compare': 'Compare',
  'villas-compare': 'Compare',
  'plots-compare': 'Compare',
  'rents-apts-compare': 'Compare',
  'rents-villas-compare': 'Compare',
  'thank-you-page': 'Thank You',
  'apts-thank-for-contacting-apartments': 'Thank You',
  'villas-thank-for-contacting-apartments': 'Thank You',
  'plots-thank-for-contacting-apartments': 'Thank You',
  'thank-for-contacting-apartments': 'Thank You',
  'rents-thank-for-contacting-apartments': 'Thank You',
  'apts-explore-properties-posted-by-agents-page': 'Explore by Agents',
  'villas-explore-properties-posted-by-agents-page': 'Explore by Agents',
  'plots-explore-properties-posted-by-agents-page': 'Explore by Agents',
  'apts-user-location': 'Near Me',
  'villas-user-location': 'Near Me',
  'plots-user-location': 'Near Me',
  'apts-hot-localities-page': 'Hot Localities',
  'villas-hot-localities-page': 'Hot Localities',
  'plots-hot-localities-page': 'Hot Localities',
  'apts-property-news-page': 'Property News',
  'villas-property-news-page': 'Property News',
  'plots-property-news-page': 'Property News',
  'apts-projects-by-builder': 'Builders',
  'aptspropertypagebuilder': 'Builders',
  'villas-projects-by-builder': 'Builders',
  'plots-projects-by-builder': 'Builders',
};

/** Humanize a slug for display */
export function slugToLabel(slug) {
  if (!slug || typeof slug !== 'string') return '';
  return slug
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Normalize a label for URL (e.g. "Kokapet" → "kokapet") */
function slugifyForUrl(label) {
  if (!label || typeof label !== 'string') return '';
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

const TYPE_CONFIG = {
  apartment: { label: 'Apartments', homePath: '/apts-home', searchPath: '/apts-search-screen', resalePath: '/apts-resale3', resalePropertyPath: '/apts-resale-property-page' },
  villa: { label: 'Villas', homePath: '/villas-home', searchPath: '/villas-search-screen', resalePath: '/villas-resale3', resalePropertyPath: '/villas-resale-property-page' },
  plot: { label: 'Plots', homePath: '/plots-home', searchPath: '/plots-search-screen', resalePath: '/plots-resale3', resalePropertyPath: '/plots-resale-property-page' },
};

/** Return the location/search screen path for this type (never home). Used so City/Location breadcrumb links go to search screen, not WelcomeProser. */
function getSearchPathForCategoryAndType(category, type) {
  if (category === 'Rent') {
    if (type === 'villa') return '/rents-villas-search-screen';
    return '/rents-apts-search-screen';
  }
  if (category === 'Resale') {
    if (type === 'villa') return '/villas-resale3';
    if (type === 'plot') return '/plots-resale3';
    return '/apts-resale3';
  }
  if (type && TYPE_CONFIG[type]) return TYPE_CONFIG[type].searchPath;
  return '/apts-search-screen';
}

/**
 * Derive category and type from pathname.
 * @returns {{ category: 'Buy'|'Rent'|'Resale'|null, type: 'apartment'|'villa'|'plot'|null, typeLabel: string, categoryPath: string, typePath: string }}
 */
function getCategoryAndType(pathname) {
  const p = pathname.replace(/^\//, '').toLowerCase();
  const segment = p.split('/')[0] || '';

  // Rent
  if (segment.startsWith('rents-') || segment === 'rent-apartments-home' || segment === 'rent-villas-home') {
    if (segment.includes('villa')) return { category: 'Rent', type: 'villa', typeLabel: 'Villas', categoryPath: '/rents-home', typePath: '/rents-home' };
    return { category: 'Rent', type: 'apartment', typeLabel: 'Apartments', categoryPath: '/rents-home', typePath: '/rents-home' };
  }
  if (segment === 'rents-home') return { category: 'Rent', type: null, typeLabel: '', categoryPath: '/rents-home', typePath: '/rents-home' };

  // Resale
  if (segment.includes('resale')) {
    if (segment.startsWith('villas-resale')) return { category: 'Resale', type: 'villa', typeLabel: 'Villas', categoryPath: '/villas-resale3', typePath: '/villas-resale3', resalePropertyPath: '/villas-resale-property-page' };
    if (segment.startsWith('plots-resale')) return { category: 'Resale', type: 'plot', typeLabel: 'Plots', categoryPath: '/plots-resale3', typePath: '/plots-resale3', resalePropertyPath: '/plots-resale-property-page' };
    return { category: 'Resale', type: 'apartment', typeLabel: 'Apartments', categoryPath: '/apts-resale3', typePath: '/apts-resale3', resalePropertyPath: '/apts-resale-property-page' };
  }

  // Buy: apartments
  if (segment === 'apts-home' || segment === 'apts-search-screen' || segment === 'projects' || segment === 'allprojects' || segment === 'mapsprojects' || segment.startsWith('apts-')) {
    const config = TYPE_CONFIG.apartment;
    return { category: 'Buy', type: 'apartment', typeLabel: config.label, categoryPath: config.homePath, typePath: config.homePath };
  }
  // Buy: villas
  if (segment === 'villas-home' || segment === 'villas-search-screen' || segment === 'villa-projects' || segment === 'allvillas' || segment === 'mapvillas' || segment.startsWith('villas-')) {
    const config = TYPE_CONFIG.villa;
    return { category: 'Buy', type: 'villa', typeLabel: config.label, categoryPath: config.homePath, typePath: config.homePath };
  }
  // Buy: plots
  if (segment === 'plots-home' || segment === 'plots-search-screen' || segment === 'plot-projects' || segment === 'allplots' || segment === 'mapplots' || segment.startsWith('plots-')) {
    const config = TYPE_CONFIG.plot;
    return { category: 'Buy', type: 'plot', typeLabel: config.label, categoryPath: config.homePath, typePath: config.homePath };
  }

  return { category: null, type: null, typeLabel: '', categoryPath: '/', typePath: '/' };
}

/**
 * Build breadcrumb items in fixed order: Home → Category → Type → City → Location → Project.
 * Each segment is included when present (from URL or context); project pages show full trail when params exist.
 * @param {object} [locationOverrides] - Optional { city, location } from project data when URL has no city/location.
 * @param {string} [overrideCategoryLabel] - When categoryname in URL is a numeric ID, pass the category display name here so breadcrumb shows name instead of ID.
 */
export function getOrderedBreadcrumbItems(pathname, searchParams, params = {}, overrideLabel = null, locationOverrides = null, overrideCategoryLabel = null) {
  const items = [];
  const isHome = pathname === '/' || pathname === '' || ['/apts-home', '/villas-home', '/plots-home'].includes(pathname);
  if (isHome) return items;

  const search = typeof searchParams.get === 'function' ? searchParams : new URLSearchParams(searchParams);
  const categoryname = search.get('categoryname') || search.get('category') || '';
  const citySlug = search.get('city') || '';
  const locationSlug = search.get('location') || '';
  const multiParam = search.get('multi') || '';
  const multiQueries = decodeMultiParam(multiParam);
  const slug = params.slug || '';

  // City and location: prefer project/page overrides over URL so project detail shows its actual location (e.g. Kondapur), not the location from the link (e.g. Kollur)
  const cityFromMulti = multiQueries.length > 0 && multiQueries.find((q) => String(q.kind || '').toLowerCase() === 'city');
  const baseCity =
    (locationOverrides?.city ? slugifyForUrl(locationOverrides.city) : '') ||
    citySlug ||
    (cityFromMulti ? slugifyForUrl(cityFromMulti.query || cityFromMulti.label || '') : '');
  const effectiveLocation =
    (locationOverrides?.location ? slugifyForUrl(locationOverrides.location) : '') ||
    locationSlug;
  // When we have location but no city, show city before location: derive city from location lookup or default
  const derivedCity =
    effectiveLocation && !baseCity
      ? (getCityForLocationSync(effectiveLocation)?.citySlug ?? 'hyderabad')
      : '';
  const effectiveCity = baseCity || derivedCity;
  const hasMultiLocations = multiQueries.length >= 2;

  const { category, type, typeLabel, typePath } = getCategoryAndType(pathname);
  const pathFirst = pathname.replace(/^\//, '').split('/')[0] || '';

  // 1. Home
  items.push({ label: 'Home', path: '/', isCurrent: false });

  // Build search path for city/location links: always use the location/search screen (never home),
  // so clicking City, Type, or Category (when city/location exists) goes to search screen, not WelcomeProser home.
  const searchScreenBase = getSearchPathForCategoryAndType(category, type);
  const buildSearchPath = (extra = {}) => {
    const q = new URLSearchParams();
    q.set('type', type === 'apartment' ? 'apartment_buy' : type === 'villa' ? 'villa_buy' : 'plot_buy');
    if (categoryname) q.set('categoryname', categoryname);
    if (effectiveCity) q.set('city', effectiveCity);
    if (extra.location != null) q.set('location', extra.location);
    Object.entries(extra).filter(([k, v]) => k !== 'location' && v != null).forEach(([k, v]) => q.set(k, v));
    if (multiParam) q.set('multi', multiParam);
    const query = q.toString();
    const suffix = query ? `?${query}` : '';
    return `${searchScreenBase}${suffix}`;
  };

  // When we have city or location in the trail, Category and Type links must go to search screen (not home).
  const useSearchScreenForNav = !!(effectiveCity || effectiveLocation);

  // 2. Category (when categoryname in URL – e.g. Laaarge Homes, or ID resolved to name)
  if (categoryname) {
    const isNumericId = /^\d+$/.test(String(categoryname).trim());
    const categoryLabel =
      (isNumericId && overrideCategoryLabel && String(overrideCategoryLabel).trim())
        ? String(overrideCategoryLabel).trim()
        : slugToLabel(categoryname);
    const categoryQuery = new URLSearchParams();
    categoryQuery.set('type', type === 'apartment' ? 'apartment_buy' : type === 'villa' ? 'villa_buy' : 'plot_buy');
    if (effectiveCity) categoryQuery.set('city', effectiveCity);
    categoryQuery.set('categoryname', categoryname);
    if (effectiveLocation) categoryQuery.set('location', effectiveLocation);
    if (multiParam) categoryQuery.set('multi', multiParam);
    const isSearchScreen = ['apts-search-screen', 'villas-search-screen', 'plots-search-screen', 'rents-apts-search-screen', 'rents-villas-search-screen'].includes(pathFirst);
    let categoryPath;
    if (isSearchScreen) {
      categoryPath = `${pathname.split('?')[0]}?${categoryQuery.toString()}`;
    } else if (useSearchScreenForNav) {
      categoryPath = `${searchScreenBase}?${categoryQuery.toString()}`;
    } else if (pathFirst === 'apts-home' || pathFirst === 'villas-home' || pathFirst === 'plots-home') {
      categoryPath = `${typePath}?${categoryQuery.toString()}`;
    } else if (pathFirst.startsWith('rents-')) {
      categoryPath = `/rents-home?${categoryQuery.toString()}`;
    } else {
      categoryPath = `${typePath}?${categoryQuery.toString()}`;
    }
    const isCategoryOnly = (pathFirst === 'apts-home' || pathFirst === 'villas-home' || pathFirst === 'plots-home') && !slug && !effectiveCity && !effectiveLocation;
    items.push({
      label: categoryLabel,
      path: categoryPath,
      isCurrent: isCategoryOnly,
    });
  }

  // 3. Type (Apartments / Villas / Plots) — always link to HOME so clicking "Apartments" goes to home page only
  if (type && typeLabel) {
    const onHomeWithCategory = ['apts-home', 'villas-home', 'plots-home'].includes(pathFirst) && categoryname;
    const isTypeCurrent = !slug && !effectiveCity && !effectiveLocation && !onHomeWithCategory;
    items.push({
      label: typeLabel,
      path: typePath,
      isCurrent: isTypeCurrent,
    });
  }

  // 4. City (when city in URL or from project data) — link to HOME so user can select the city again
  if (effectiveCity) {
    const cityLabel =
      citySlug ? slugToLabel(citySlug)
      : (locationOverrides?.city && typeof locationOverrides.city === 'string') ? locationOverrides.city
      : slugToLabel(effectiveCity);
    const typeParamForHome = category === 'Rent'
      ? (type === 'villa' ? 'villa_rent' : 'apartment_rent')
      : (type === 'apartment' ? 'apartment_buy' : type === 'villa' ? 'villa_buy' : 'plot_buy');
    const homeQuery = new URLSearchParams();
    homeQuery.set('type', typeParamForHome);
    if (categoryname) homeQuery.set('categoryname', categoryname);
    if (effectiveCity) homeQuery.set('city', effectiveCity);
    const pathWithCity = `${typePath}?${homeQuery.toString()}`;
    items.push({
      label: typeof cityLabel === 'string' ? cityLabel : slugToLabel(effectiveCity),
      path: pathWithCity,
      isCurrent: !hasMultiLocations && !effectiveLocation && !slug,
    });
  }

  // 5. Location(s): single location OR multiple locations from multi param
  if (hasMultiLocations) {
    const labels = multiQueries.map((q) => slugToLabel(q.label || q.query || '')).filter(Boolean);
    const locationLabel = labels.join(', ');
    const currentSearch = typeof search.toString === 'function' ? search.toString() : new URLSearchParams(search).toString();
    const pathWithMulti = currentSearch ? `${pathname}?${currentSearch}` : pathname;
    items.push({
      label: locationLabel,
      path: pathWithMulti,
      isCurrent: !slug,
    });
  } else if (effectiveLocation) {
    const locationLabel = locationSlug ? slugToLabel(locationSlug) : (locationOverrides?.location || effectiveLocation);
    const pathWithLocation = buildSearchPath({ location: effectiveLocation });
    items.push({
      label: typeof locationLabel === 'string' ? locationLabel : slugToLabel(effectiveLocation),
      path: pathWithLocation,
      isCurrent: !slug,
    });
  }

  // 6. Project (detail page)
  if (slug) {
    const projectLabel = overrideLabel && String(overrideLabel).trim() ? overrideLabel : slugToLabel(slug);
    const searchSuffix =
      searchParams && typeof searchParams.toString === 'function' && searchParams.toString()
        ? `?${searchParams.toString()}`
        : '';
    items.push({
      label: projectLabel,
      path: `${pathname}${searchSuffix}`,
      isCurrent: true,
    });
  }

  // For non-property routes (e.g. create-account, about-company)
  if (!type && items.length === 1) {
    const firstSegment = pathname.replace(/^\//, '').split('/')[0] || '';
    const label = ROUTE_LABELS[firstSegment] || slugToLabel(firstSegment) || firstSegment;
    items.push({ label: label || 'Page', path: pathname, isCurrent: true });
  }

  // Ensure exactly one current
  if (items.length > 0 && !items.some((i) => i.isCurrent)) {
    items[items.length - 1].isCurrent = true;
  }

  return items;
}

/**
 * Legacy: build breadcrumb items from path segments (used for non-standard pages).
 * @param {string} pathname
 * @param {object} params
 * @param {string} [overrideCurrent]
 */
export function getBreadcrumbItems(pathname, params = {}, overrideCurrent = null) {
  const ordered = getOrderedBreadcrumbItems(pathname, new URLSearchParams(), params, overrideCurrent);
  if (ordered.length > 0) return ordered;

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  const items = [{ label: 'Home', path: '/', isCurrent: false }];
  let acc = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    acc += (acc ? '/' : '') + seg;
    const fullPath = '/' + acc;
    let label = ROUTE_LABELS[seg] || ROUTE_LABELS[fullPath];
    if (label == null && i === segments.length - 1 && params.slug) {
      label = overrideCurrent && String(overrideCurrent).trim() ? overrideCurrent : slugToLabel(params.slug);
    } else if (label == null) {
      label = slugToLabel(seg);
    }
    if (i === segments.length - 1 && overrideCurrent && String(overrideCurrent).trim()) label = overrideCurrent;
    items.push({ label: label || seg, path: fullPath, isCurrent: i === segments.length - 1 });
  }
  if (items.length > 1) items[items.length - 1].isCurrent = true;
  return items;
}
